import { API, graphqlOperation } from 'aws-amplify';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import { captureError } from '../common/sentry';
import {
  addQmComment,
  onAddQmComment,
  onCreateQmRecord,
  onUpdateQmRecord,
  createQmRecord,
  getQmDashboardSummary,
  getQmRecord,
  listQmRecords,
  updateQmRecord,
  listQmReferenceReasons,
  getQmFeatureFlags,
  setQmFeatureFlags,
  onSetQmFeatureFlags,
  listQmReferenceClosureCodes,
  listQmReferenceLocations,
  listQmReferenceLocationsAdmin,
  createQmReferenceLocation,
  updateQmReferenceLocation,
  archiveQmReferenceLocation,
  getUploadUrl,
  listAttachments,
  confirmAttachment
} from '../queries/queries';

export type QmCrudStatus = 'CREATED' | 'PENDING' | 'CLOSED';
export type QmRecordType = 'INBOUND' | 'SITE_INBOUND';

export type QmStatusHistoryEntry = {
  id: number;
  fromStatus: QmCrudStatus | null;
  toStatus: QmCrudStatus;
  changedBy: string;
  changedAt: string;
  note: string | null;
};

export type QmChangeHistoryEntry = {
  id: number;
  fieldName: string;
  oldValue: string | null;
  newValue: string | null;
  changedBy: string;
  changedAt: string;
};

export type QmCommentEntry = {
  id: number;
  body: string;
  createdBy: string;
  createdAt: string;
};

export type QmPoLineDetail = {
  lineIndex: number;
  poLineId: string | null;
  poLine: string | null;
  itemId: string | null;
  linePayload: Record<string, unknown> | null;
};

export type QmPackageDetail = {
  packageNumber: number;
  packageType: string | null;
};

export type QmRecord = {
  qmId: number;
  qmNumber: string;
  type: QmRecordType;
  status: QmCrudStatus;
  reasonCode: string;
  reasonLabel: string;
  closureCode: string | null;
  closureLabel: string | null;
  sscc: string | null;
  asn: string | null;
  poNumber: string | null;
  poLine: string | null;
  poLineId: string | null;
  itemId: string | null;
  supplierCode: string | null;
  supplierName: string | null;
  contactName: string | null;
  itemLocation: string;
  packageCount: number | null;
  packageType: string | null;
  otherInformation: string | null;
  description: string | null;
  closureNotes: string | null;
  createdBy: string;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string | null;
  closedAt: string | null;
  statusHistory: QmStatusHistoryEntry[];
  changeHistory: QmChangeHistoryEntry[];
  comments: QmCommentEntry[];
  poLines: QmPoLineDetail[];
  packages: QmPackageDetail[];
};

export type QmPoLineDetailInput = {
  poLineId?: string | null;
  poLine?: string | null;
  itemId?: string | null;
  linePayload?: Record<string, unknown> | string | null;
};

export type QmPackageDetailInput = {
  packageNumber: number;
  packageType?: string | null;
};

export type ListQmRecordsFilterInput = {
  status?: QmCrudStatus | null;
  reasonCode?: string | null;
  closureCode?: string | null;
  createdBy?: string | null;
  itemLocation?: string | null;
  supplierName?: string | null;
  poNumber?: string | null;
  qmNumber?: string | null;
  createdFrom?: string | null;
  createdTo?: string | null;
  search?: string | null;
};

export type QmRecordPageResult = {
  items: QmRecord[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type QmDashboardSummaryResult = {
  total: number;
  created: number;
  pending: number;
  closed: number;
};

export type QmFeatureFlagsResult = {
  qmNewDashboardEnabled: boolean;
  qmSiteInboundEnabled: boolean;
};

export type AddQmCommentInput = {
  qmId: number;
  body: string;
  createdBy?: string | null;
};

export type CreateQmRecordInput = {
  type?: QmRecordType | null;
  reasonCode: string;
  sscc?: string | null;
  asn?: string | null;
  poNumber?: string | null;
  poLine?: string | null;
  poLineId?: string | null;
  itemId?: string | null;
  supplierCode?: string | null;
  supplierName?: string | null;
  contactName?: string | null;
  itemLocation: string;
  packageCount?: number | null;
  packageType?: string | null;
  otherInformation?: string | null;
  description?: string | null;
  poLines?: QmPoLineDetailInput[] | null;
  packages?: QmPackageDetailInput[] | null;
  createdBy?: string | null;
};

export type UpdateQmRecordInput = {
  qmId: number;
  status?: QmCrudStatus | null;
  reasonCode?: string | null;
  closureCode?: string | null;
  sscc?: string | null;
  asn?: string | null;
  poNumber?: string | null;
  poLine?: string | null;
  poLineId?: string | null;
  itemId?: string | null;
  supplierCode?: string | null;
  supplierName?: string | null;
  contactName?: string | null;
  itemLocation?: string | null;
  packageCount?: number | null;
  packageType?: string | null;
  otherInformation?: string | null;
  description?: string | null;
  closureNotes?: string | null;
  updatedBy?: string | null;
  note?: string | null;
};

type CreateQmRecordMutation = {
  createQmRecord: QmRecord;
};

type UpdateQmRecordMutation = {
  updateQmRecord: QmRecord;
};

type GetQmRecordQuery = {
  getQmRecord: QmRecord;
};

type ListQmRecordsQuery = {
  listQmRecords: QmRecordPageResult;
};

type GetQmDashboardSummaryQuery = {
  getQmDashboardSummary: QmDashboardSummaryResult;
};

type GetQmFeatureFlagsQuery = {
  getQmFeatureFlags: QmFeatureFlagsResult;
};

type SetQmFeatureFlagsMutation = {
  setQmFeatureFlags: QmFeatureFlagsResult;
};

type AddQmCommentMutation = {
  addQmComment: QmRecord;
};

export type QmReasonOption = {
  code: string;
  label: string;
  displayOrder: number;
};

export type QmClosureCodeOption = {
  code: string;
  label: string;
  reasonCode: string;
  displayOrder: number;
};

export type QmLocationOption = {
  code: string;
  label: string;
  displayOrder: number;
};

export type QmLocationAdminOption = {
  code: string;
  label: string;
  displayOrder: number;
  active: boolean;
};

export type CreateQmLocationInput = {
  code: string;
  label: string;
  displayOrder?: number | null;
};

export type UpdateQmLocationInput = {
  code: string;
  label: string;
  displayOrder?: number | null;
};

export type ArchiveQmLocationInput = {
  code: string;
};

type ListQmReferenceReasonsQuery = {
  listQmReferenceReasons: QmReasonOption[];
};

type ListQmReferenceClosureCodesQuery = {
  listQmReferenceClosureCodes: QmClosureCodeOption[];
};

type ListQmReferenceLocationsQuery = {
  listQmReferenceLocations: QmLocationOption[];
};

type ListQmReferenceLocationsAdminQuery = {
  listQmReferenceLocationsAdmin: QmLocationAdminOption[];
};

type CreateQmReferenceLocationMutation = {
  createQmReferenceLocation: QmLocationAdminOption;
};

type UpdateQmReferenceLocationMutation = {
  updateQmReferenceLocation: QmLocationAdminOption;
};

type ArchiveQmReferenceLocationMutation = {
  archiveQmReferenceLocation: QmLocationAdminOption;
};

function parseAwsJsonObject(value: unknown): Record<string, unknown> | null {
  if (!value) return null;

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }

  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function normalizeQmRecord(record: QmRecord): QmRecord {
  return {
    ...record,
    poLines: (record.poLines || []).map((line) => ({
      ...line,
      linePayload: parseAwsJsonObject(line.linePayload as unknown)
    }))
  };
}

function toGraphQlCreateInput(input: CreateQmRecordInput): Record<string, unknown> {
  return {
    ...input,
    poLines: (input.poLines || []).map((line) => ({
      ...line,
      linePayload:
        line.linePayload === undefined || line.linePayload === null
          ? null
          : typeof line.linePayload === 'string'
            ? line.linePayload
            : JSON.stringify(line.linePayload)
    }))
  };
}

export async function createQmRecordRequest(input: CreateQmRecordInput): Promise<QmRecord> {
  try {
    const graphQlInput = toGraphQlCreateInput(input);
    const result = (await API.graphql(
      graphqlOperation(createQmRecord, { input: graphQlInput })
    )) as GraphQLResult<CreateQmRecordMutation>;

    if (!result.data?.createQmRecord) throw new Error('No data returned from createQmRecord');
    return normalizeQmRecord(result.data.createQmRecord);
  } catch (error) {
    console.error('Error creating QM record:', error);
    captureError(error, 'qmCrud-createQmRecord');
    throw error;
  }
}

export async function updateQmRecordRequest(input: UpdateQmRecordInput): Promise<QmRecord> {
  try {
    const result = (await API.graphql(
      graphqlOperation(updateQmRecord, { input })
    )) as GraphQLResult<UpdateQmRecordMutation>;

    if (!result.data?.updateQmRecord) throw new Error('No data returned from updateQmRecord');
    return normalizeQmRecord(result.data.updateQmRecord);
  } catch (error) {
    console.error('Error updating QM record:', error);
    captureError(error, 'qmCrud-updateQmRecord');
    throw error;
  }
}

export async function getQmRecordRequest({
  qmId,
  qmNumber
}: {
  qmId?: number;
  qmNumber?: string;
}): Promise<QmRecord | null> {
  try {
    const result = (await API.graphql(
      graphqlOperation(getQmRecord, { qmId, qmNumber })
    )) as GraphQLResult<GetQmRecordQuery>;

    return result.data?.getQmRecord ? normalizeQmRecord(result.data.getQmRecord) : null;
  } catch (error) {
    console.error('Error retrieving QM record:', error);
    captureError(error, 'qmCrud-getQmRecord');
    throw error;
  }
}

export async function listQmRecordsRequest(args: {
  page?: number;
  pageSize?: number;
  filter?: ListQmRecordsFilterInput | null;
}): Promise<QmRecordPageResult> {
  try {
    const result = (await API.graphql(
      graphqlOperation(listQmRecords, {
        page: args.page ?? 1,
        pageSize: args.pageSize ?? 10,
        filter: args.filter ?? null
      })
    )) as GraphQLResult<ListQmRecordsQuery>;

    if (!result.data?.listQmRecords) {
      throw new Error('No data returned from listQmRecords');
    }

    return {
      ...result.data.listQmRecords,
      items: (result.data.listQmRecords.items || []).map((item) => normalizeQmRecord(item))
    };
  } catch (error) {
    console.error('Error listing QM records:', error);
    captureError(error, 'qmCrud-listQmRecords');
    throw error;
  }
}

export async function getQmDashboardSummaryRequest(
  filter?: ListQmRecordsFilterInput | null
): Promise<QmDashboardSummaryResult> {
  try {
    const result = (await API.graphql(
      graphqlOperation(getQmDashboardSummary, { filter: filter ?? null })
    )) as GraphQLResult<GetQmDashboardSummaryQuery>;

    if (!result.data?.getQmDashboardSummary) {
      throw new Error('No data returned from getQmDashboardSummary');
    }

    return result.data.getQmDashboardSummary;
  } catch (error) {
    console.error('Error getting QM dashboard summary:', error);
    captureError(error, 'qmCrud-getQmDashboardSummary');
    throw error;
  }
}

export async function getQmFeatureFlagsRequest(): Promise<QmFeatureFlagsResult> {
  try {
    const result = (await API.graphql(graphqlOperation(getQmFeatureFlags))) as GraphQLResult<GetQmFeatureFlagsQuery>;

    if (!result.data?.getQmFeatureFlags) {
      throw new Error('No data returned from getQmFeatureFlags');
    }

    return {
      qmNewDashboardEnabled: result.data.getQmFeatureFlags.qmNewDashboardEnabled,
      qmSiteInboundEnabled: result.data.getQmFeatureFlags.qmSiteInboundEnabled ?? false
    };
  } catch (error) {
    console.error('Error getting QM feature flags:', error);
    captureError(error, 'qmCrud-getQmFeatureFlags');
    throw error;
  }
}

export async function setQmFeatureFlagsRequest(input: QmFeatureFlagsResult): Promise<QmFeatureFlagsResult> {
  try {
    const result = (await API.graphql(
      graphqlOperation(setQmFeatureFlags, { input })
    )) as GraphQLResult<SetQmFeatureFlagsMutation>;

    if (!result.data?.setQmFeatureFlags) {
      throw new Error('No data returned from setQmFeatureFlags');
    }

    return result.data.setQmFeatureFlags;
  } catch (error) {
    console.error('Error setting QM feature flags:', error);
    captureError(error, 'qmCrud-setQmFeatureFlags');
    throw error;
  }
}

export async function addQmCommentRequest(input: AddQmCommentInput): Promise<QmRecord> {
  try {
    const result = (await API.graphql(
      graphqlOperation(addQmComment, { input })
    )) as GraphQLResult<AddQmCommentMutation>;

    if (!result.data?.addQmComment) {
      throw new Error('No data returned from addQmComment');
    }

    return normalizeQmRecord(result.data.addQmComment);
  } catch (error) {
    console.error('Error adding QM comment:', error);
    captureError(error, 'qmCrud-addQmComment');
    throw error;
  }
}

export async function listQmReferenceReasonsRequest(typeCode?: QmRecordType | null): Promise<QmReasonOption[]> {
  try {
    const result = (await API.graphql(
      graphqlOperation(listQmReferenceReasons, { typeCode })
    )) as GraphQLResult<ListQmReferenceReasonsQuery>;
    return result.data?.listQmReferenceReasons ?? [];
  } catch (error) {
    console.error('Error retrieving QM reasons:', error);
    captureError(error, 'qmCrud-listQmReferenceReasons');
    throw error;
  }
}

export async function listQmReferenceClosureCodesRequest(reasonCode?: string | null): Promise<QmClosureCodeOption[]> {
  try {
    const result = (await API.graphql(
      graphqlOperation(listQmReferenceClosureCodes, { reasonCode: reasonCode ?? null })
    )) as GraphQLResult<ListQmReferenceClosureCodesQuery>;

    return result.data?.listQmReferenceClosureCodes ?? [];
  } catch (error) {
    console.error('Error retrieving QM closure codes:', error);
    captureError(error, 'qmCrud-listQmReferenceClosureCodes');
    throw error;
  }
}

export async function listQmReferenceLocationsRequest(): Promise<QmLocationOption[]> {
  try {
    const result = (await API.graphql(
      graphqlOperation(listQmReferenceLocations)
    )) as GraphQLResult<ListQmReferenceLocationsQuery>;

    return result.data?.listQmReferenceLocations ?? [];
  } catch (error) {
    console.error('Error retrieving QM locations:', error);
    captureError(error, 'qmCrud-listQmReferenceLocations');
    throw error;
  }
}

export async function listQmReferenceLocationsAdminRequest(): Promise<QmLocationAdminOption[]> {
  try {
    const result = (await API.graphql(
      graphqlOperation(listQmReferenceLocationsAdmin)
    )) as GraphQLResult<ListQmReferenceLocationsAdminQuery>;

    return result.data?.listQmReferenceLocationsAdmin ?? [];
  } catch (error) {
    console.error('Error retrieving QM location admin list:', error);
    captureError(error, 'qmCrud-listQmReferenceLocationsAdmin');
    throw error;
  }
}

export async function createQmReferenceLocationRequest(input: CreateQmLocationInput): Promise<QmLocationAdminOption> {
  try {
    const result = (await API.graphql(
      graphqlOperation(createQmReferenceLocation, { input })
    )) as GraphQLResult<CreateQmReferenceLocationMutation>;

    if (!result.data?.createQmReferenceLocation) {
      throw new Error('No data returned from createQmReferenceLocation');
    }

    return result.data.createQmReferenceLocation;
  } catch (error) {
    console.error('Error creating QM location:', error);
    captureError(error, 'qmCrud-createQmReferenceLocation');
    throw error;
  }
}

export async function updateQmReferenceLocationRequest(input: UpdateQmLocationInput): Promise<QmLocationAdminOption> {
  try {
    const result = (await API.graphql(
      graphqlOperation(updateQmReferenceLocation, { input })
    )) as GraphQLResult<UpdateQmReferenceLocationMutation>;

    if (!result.data?.updateQmReferenceLocation) {
      throw new Error('No data returned from updateQmReferenceLocation');
    }

    return result.data.updateQmReferenceLocation;
  } catch (error) {
    console.error('Error updating QM location:', error);
    captureError(error, 'qmCrud-updateQmReferenceLocation');
    throw error;
  }
}

export async function archiveQmReferenceLocationRequest(input: ArchiveQmLocationInput): Promise<QmLocationAdminOption> {
  try {
    const result = (await API.graphql(
      graphqlOperation(archiveQmReferenceLocation, { input })
    )) as GraphQLResult<ArchiveQmReferenceLocationMutation>;

    if (!result.data?.archiveQmReferenceLocation) {
      throw new Error('No data returned from archiveQmReferenceLocation');
    }

    return result.data.archiveQmReferenceLocation;
  } catch (error) {
    console.error('Error archiving QM location:', error);
    captureError(error, 'qmCrud-archiveQmReferenceLocation');
    throw error;
  }
}

export type QmAttachment = {
  id: number;
  qmId: number;
  fileName: string;
  mimeType: string | null;
  sizeBytes: number | null;
  uploadedBy: string;
  uploadedAt: string;
  downloadUrl: string;
};

export type QmLiveUpdateType = 'CREATED' | 'UPDATED' | 'COMMENTED';

export type QmLiveUpdateEvent = {
  type: QmLiveUpdateType;
  record: QmRecord;
};

type QmSubscriptionFieldName = 'onCreateQmRecord' | 'onUpdateQmRecord' | 'onAddQmComment';

type QmSubscriptionPayload = {
  value?: {
    data?: Partial<Record<QmSubscriptionFieldName, QmRecord>>;
  };
};

type QmSubscription = {
  unsubscribe: () => void;
};

type SubscriptionObserver<TPayload> = {
  next: (payload: TPayload) => void;
  error: (error: unknown) => void;
};

type SubscriptionStream<TPayload> = {
  subscribe: (observer: SubscriptionObserver<TPayload>) => QmSubscription;
};

function isSubscriptionStream<TPayload = unknown>(value: unknown): value is SubscriptionStream<TPayload> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'subscribe' in value &&
    typeof (value as { subscribe?: unknown }).subscribe === 'function'
  );
}

export type ConfirmAttachmentInput = {
  qmId: number;
  s3Key: string;
  fileName: string;
  mimeType?: string | null;
  sizeBytes?: number | null;
  uploadedBy?: string | null;
};

type GetUploadUrlQuery = {
  getUploadUrl: { uploadUrl: string; s3Key: string };
};

type ListAttachmentsQuery = {
  listAttachments: QmAttachment[];
};

type ConfirmAttachmentMutation = {
  confirmAttachment: QmAttachment;
};

type QmFeatureFlagsSubscriptionPayload = {
  value?: {
    data?: {
      onSetQmFeatureFlags?: QmFeatureFlagsResult;
    };
  };
};

export async function getUploadUrlRequest(args: {
  qmId: number;
  fileName: string;
  mimeType?: string | null;
}): Promise<{ uploadUrl: string; s3Key: string }> {
  try {
    const result = (await API.graphql(graphqlOperation(getUploadUrl, args))) as GraphQLResult<GetUploadUrlQuery>;

    if (!result.data?.getUploadUrl) throw new Error('No data returned from getUploadUrl');
    return result.data.getUploadUrl;
  } catch (error) {
    console.error('Error getting upload URL:', error);
    captureError(error, 'qmCrud-getUploadUrl');
    throw error;
  }
}

export async function uploadFileToS3(uploadUrl: string, file: File): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' }
  });
  if (!response.ok) {
    throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
  }
}

export async function confirmAttachmentRequest(input: ConfirmAttachmentInput): Promise<QmAttachment> {
  try {
    const result = (await API.graphql(
      graphqlOperation(confirmAttachment, { input })
    )) as GraphQLResult<ConfirmAttachmentMutation>;

    if (!result.data?.confirmAttachment) throw new Error('No data returned from confirmAttachment');
    return result.data.confirmAttachment;
  } catch (error) {
    console.error('Error confirming attachment:', error);
    captureError(error, 'qmCrud-confirmAttachment');
    throw error;
  }
}

export async function listAttachmentsRequest(qmId: number): Promise<QmAttachment[]> {
  try {
    const result = (await API.graphql(
      graphqlOperation(listAttachments, { qmId })
    )) as GraphQLResult<ListAttachmentsQuery>;

    return result.data?.listAttachments ?? [];
  } catch (error) {
    console.error('Error listing attachments:', error);
    captureError(error, 'qmCrud-listAttachments');
    throw error;
  }
}

export function subscribeQmLiveUpdates(onEvent: (event: QmLiveUpdateEvent) => void): () => void {
  const liveSubscriptionDefinitions: Array<{
    type: QmLiveUpdateType;
    query: string;
    fieldName: QmSubscriptionFieldName;
  }> = [
    {
      type: 'CREATED',
      query: onCreateQmRecord,
      fieldName: 'onCreateQmRecord'
    },
    {
      type: 'UPDATED',
      query: onUpdateQmRecord,
      fieldName: 'onUpdateQmRecord'
    },
    {
      type: 'COMMENTED',
      query: onAddQmComment,
      fieldName: 'onAddQmComment'
    }
  ];

  let isDisposed = false;
  let reconnectAttempts = 0;
  let reconnectTimeoutId: number | null = null;
  let subscriptions: QmSubscription[] = [];

  const clearReconnectTimer = () => {
    if (reconnectTimeoutId !== null) {
      window.clearTimeout(reconnectTimeoutId);
      reconnectTimeoutId = null;
    }
  };

  const unsubscribeAll = () => {
    subscriptions.forEach((subscription) => {
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    });
    subscriptions = [];
  };

  const scheduleReconnect = () => {
    if (isDisposed || reconnectTimeoutId !== null) return;

    const delayMs = Math.min(30000, 1000 * 2 ** reconnectAttempts);
    reconnectAttempts += 1;

    reconnectTimeoutId = window.setTimeout(() => {
      reconnectTimeoutId = null;
      startSubscriptions();
    }, delayMs);
  };

  const startSubscriptions = () => {
    if (isDisposed) return;

    unsubscribeAll();

    subscriptions = liveSubscriptionDefinitions
      .map(({ type, query, fieldName }): QmSubscription | null => {
        const stream = API.graphql(graphqlOperation(query));

        if (!isSubscriptionStream<QmSubscriptionPayload>(stream)) {
          const error = new Error(`Expected subscription stream for ${fieldName}`);
          console.error(`QM live update subscription setup error (${fieldName}):`, error);
          captureError(error, `qmCrud-subscribe-${fieldName}-setup`);
          scheduleReconnect();
          return null;
        }

        return stream.subscribe({
          next: (payload) => {
            const record = payload?.value?.data?.[fieldName];
            if (record) {
              reconnectAttempts = 0;
              onEvent({ type, record });
            }
          },
          error: (error) => {
            console.error(`QM live update subscription error (${fieldName}):`, error);
            captureError(error, `qmCrud-subscribe-${fieldName}`);
            scheduleReconnect();
          }
        });
      })
      .filter((subscription): subscription is QmSubscription => subscription !== null);
  };

  startSubscriptions();

  return () => {
    isDisposed = true;
    clearReconnectTimer();
    unsubscribeAll();
  };
}

export function subscribeQmFeatureFlagUpdates(onEvent: (flags: QmFeatureFlagsResult) => void): () => void {
  const stream = API.graphql(graphqlOperation(onSetQmFeatureFlags));

  if (!isSubscriptionStream<QmFeatureFlagsSubscriptionPayload>(stream)) {
    const error = new Error('Expected subscription stream for onSetQmFeatureFlags');
    console.error('QM feature flag subscription setup error:', error);
    captureError(error, 'qmCrud-subscribeQmFeatureFlagUpdates-setup');
    return () => {};
  }

  const subscription = stream.subscribe({
    next: (payload) => {
      const flags = payload?.value?.data?.onSetQmFeatureFlags;
      if (flags) {
        onEvent(flags);
      }
    },
    error: (error) => {
      console.error('QM feature flag subscription error:', error);
      captureError(error, 'qmCrud-subscribeQmFeatureFlagUpdates');
    }
  });

  return () => {
    if (subscription && typeof subscription.unsubscribe === 'function') {
      subscription.unsubscribe();
    }
  };
}
